import rawData from './stadium-data.json';

// Seating/device inventory for LRB sections 147 & 145, the Nines Club,
// the SMR suite and the end-zone field boxes. Extracted verbatim from the
// lrb-sections.html prototype; the `ip` fields are the join key against the
// monitored `boxes` collection.

export type SeatTv = { num: number; i0: number; i1: number };

export type SectionRow = {
  labL: number | null;
  labR: number | null;
  boxL: number | null;
  boxR: number | null;
  tvs: SeatTv[];
};

export type SectionData = { section: string; rows: SectionRow[] };

export type EzTv = { half: string; box: number; tv: number; ip: string };

export type DeviceInfo = {
  name: string;
  hd: string;
  ip: string;
  label: string;
};

export type StadiumData = {
  s147: SectionData;
  s145: SectionData;
  ez: EzTv[];
  devices: Record<string, DeviceInfo>;
};

export const STADIUM_DATA = rawData as StadiumData;

/**
 * JSON ip -> device name, for re-resolving each TV against the live `boxes`
 * collection BY NAME. The scene bakes the JSON ip into every TV at build
 * time, so an IP edited in the dashboard would otherwise orphan its TV
 * (gray status, commands sent to the old address). Names are the stable,
 * unique key; the baked ip only identifies which TV is which.
 */
export function stadiumIpNameIndex(): Map<string, string> {
  const map = new Map<string, string>();
  for (const dev of Object.values(STADIUM_DATA.devices)) {
    if (dev.ip) map.set(dev.ip, dev.name);
  }
  for (const e of STADIUM_DATA.ez) {
    if (e.ip) map.set(e.ip, `${e.half} Field Box ${e.box} TV-${e.tv}`);
  }
  return map;
}

export const FILTERS = ['ALL', '147', '145', 'NINES', 'SMR', 'EZ'] as const;
export type FilterKey = (typeof FILTERS)[number];

/** Live health of one TV, derived from the monitoring `boxes` collection. */
export type TvStatus = 'online' | 'degraded' | 'offline' | 'unknown';

export const STATUS_COLORS: Record<TvStatus, number> = {
  online: 0x00c8d2, // aqua — box reports OK
  degraded: 0xffb454, // amber — pings but the app is not responding
  offline: 0xff4d4d, // red — ping failed
  unknown: 0x5a6a74, // gray — no device on file / not monitored yet
};

// The agent writes ONLINE / COMMAND_FAIL / PING_FAIL / UNKNOWN
// (monitoring-agent src/core/collections/boxes.ts); OK / PING_SUCCESS are
// legacy values kept as fallbacks in case old documents still carry them.
export function statusFromBox(boxStatus: string | undefined): TvStatus {
  switch (boxStatus) {
    case 'ONLINE':
    case 'OK':
      return 'online';
    case 'COMMAND_FAIL':
    case 'PING_SUCCESS':
      return 'degraded';
    case 'PING_FAIL':
      return 'offline';
    default:
      return 'unknown';
  }
}
